import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Copy, MessageSquare, Share, Check } from 'lucide-react';
import { JobDTO } from '@/types';
import { SendSmsModal } from './SendSmsModal';
import { useIsMobile } from '@/hooks/useIsMobile';
import { copyToClipboard, nativeShare, showAlert } from '@/lib/capacitor';
import { apiService } from '@/lib/api';
import { useCompanyTimezone } from '@/hooks/useGlobalCompany';
import { generateJobShareText } from '@/lib/smsUtils';
import { isListItemCompletedForCalendarLock } from '@/hooks/useCompletedJobEditLock';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';

interface JobShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    job: JobDTO;
}

export function JobShareModal({ isOpen, onClose, job }: JobShareModalProps) {
    const isMobile = useIsMobile();
    const companyTz = useCompanyTimezone();
    const [showSmsModal, setShowSmsModal] = useState(false);
    const [copied, setCopied] = useState(false);
    const layer = useModalLayer({
        enabled: isOpen && !showSmsModal,
        onClose,
        historyState: { jobShareModal: true },
    });

    const { data: jobStatuses } = useQuery({
        queryKey: ['jobStatuses', job.company?.id],
        queryFn: () => (job.company?.id ? apiService.getJobStatusesByCompany(job.company.id) : Promise.resolve([])),
        enabled: isOpen && !!job.company?.id,
    });

    const isCompleted = isListItemCompletedForCalendarLock(job, jobStatuses);
    const shareText = generateJobShareText(job, isCompleted, companyTz);

    const handleCopy = async () => {
        const success = await copyToClipboard(shareText);
        if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleNativeShare = async () => {
        const result = await nativeShare({ title: `Job #${job.invoice_number}`, text: shareText });
        if (result === 'shared') {
            onClose();
        } else if (result === 'unavailable') {
            // No share sheet on this device — copy instead.
            const ok = await copyToClipboard(shareText);
            await showAlert(ok ? 'Copied to clipboard.' : 'Unable to share on this device.', 'Share');
            if (ok) onClose();
        }
    };

    const handleSmsModalClose = () => {
        setShowSmsModal(false);
        onClose();
    };

    if (!isOpen && !showSmsModal) return null;

    return (
        <LayerDepthContext.Provider value={layer.depth}>
            {isOpen && !showSmsModal && (
                <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={layer.style}>
                    <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto animate-in fade-in slide-in-from-bottom duration-200 safe-area-bottom">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-green-50 to-emerald-50">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <Share className="h-5 w-5 text-green-600" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">Share Job</h2>
                                    <p className="text-sm text-gray-500">Job #{job.invoice_number}</p>
                                </div>
                            </div>
                            <button
                                onClick={layer.onClose}
                                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                <X className="h-5 w-5 text-gray-500" />
                            </button>
                        </div>

                        {/* Share Text Preview */}
                        <div className="px-4 pt-4">
                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                <p className="text-sm text-gray-700 whitespace-pre-line">{shareText}</p>
                            </div>
                        </div>

                        {/* Options */}
                        <div className="p-4 space-y-3">
                            <p className="text-sm text-gray-600 mb-4">Choose how to share:</p>

                            {/* Copy */}
                            <button
                                onClick={handleCopy}
                                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-green-400 hover:bg-green-50 transition-all group"
                            >
                                <div className={`p-3 rounded-xl transition-colors ${copied ? 'bg-green-200' : 'bg-gray-100 group-hover:bg-gray-200'}`}>
                                    {copied ? (
                                        <Check className="h-6 w-6 text-green-600" />
                                    ) : (
                                        <Copy className="h-6 w-6 text-gray-600" />
                                    )}
                                </div>
                                <div className="text-left">
                                    <p className="font-semibold text-gray-900">
                                        {copied ? 'Copied!' : 'Copy'}
                                    </p>
                                    <p className="text-sm text-gray-500">Copy to clipboard to paste anywhere</p>
                                </div>
                            </button>

                            {/* SMS via Service Strike */}
                            <button
                                onClick={() => setShowSmsModal(true)}
                                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all group"
                            >
                                <div className="p-3 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors">
                                    <MessageSquare className="h-6 w-6 text-blue-600" />
                                </div>
                                <div className="text-left">
                                    <p className="font-semibold text-gray-900">Send via SMS</p>
                                    <p className="text-sm text-gray-500">Send SMS using your VoIP number</p>
                                </div>
                            </button>

                            {/* Native Share (mobile) */}
                            {isMobile && (
                                <button
                                    onClick={handleNativeShare}
                                    className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 transition-all group"
                                >
                                    <div className="p-3 bg-indigo-100 rounded-xl group-hover:bg-indigo-200 transition-colors">
                                        <Share className="h-6 w-6 text-indigo-600" />
                                    </div>
                                    <div className="text-left">
                                        <p className="font-semibold text-gray-900">More Options</p>
                                        <p className="text-sm text-gray-500">Share via other apps on your device</p>
                                    </div>
                                </button>
                            )}
                        </div>

                        {/* Cancel Button */}
                        <div className="p-4 pt-0">
                            <button
                                onClick={layer.onClose}
                                className="w-full py-3 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* VoIP / Service Strike SMS Modal — no preset recipient; text prefilled */}
            <SendSmsModal
                isOpen={showSmsModal}
                onClose={handleSmsModalClose}
                recipientPhone=""
                recipientName="Client"
                recipientType="Client"
                jobId={job.id}
                initialMessage={shareText}
            />
        </LayerDepthContext.Provider>
    );
}

export default JobShareModal;
